/**
 * Field grouping algorithms for Vizrt templates with 10+ fields.
 *
 * Two patterns are detected:
 * - LETTER_SUFFIX: fields like 001, 001A, 001B → grouped by base number (F1 style)
 * - NUMERIC_GROUPS: fields like 010-017, 020-027 → grouped by divisor (NFL/GE style)
 *
 * Singletons merged into "Configuração" group, "000"/"0" prefix renamed to "Geral".
 */

function deriveGroupLabel(field, index) {
  const label = field.label || field.name;
  const cleaned = label
    .replace(/[_\s]*[A-Z]?\d+$/, '')
    .replace(/[_]/g, ' ')
    .trim();

  if (cleaned.length > 0) {
    const capitalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    return `${capitalized} ${index}`;
  }
  return `Grupo ${index}`;
}

function buildGroupsFromMap(groupMap, extraFields, zeroKey) {
  const sortedKeys = Object.keys(groupMap).sort((a, b) => parseInt(a) - parseInt(b));
  const groups = [];
  const singletons = [];

  for (const key of sortedKeys) {
    const grpFields = groupMap[key];
    if (grpFields.length < 2 && sortedKeys.length > 5) {
      singletons.push(...grpFields);
    } else {
      groups.push({
        key: String(key),
        label: deriveGroupLabel(grpFields[0], groups.length + 1),
        fields: grpFields,
      });
    }
  }

  const remainingFields = [...singletons, ...extraFields];
  if (remainingFields.length > 0) {
    const configGroup = groups.find(g => g.key === zeroKey);
    if (configGroup) {
      configGroup.fields.push(...remainingFields);
    } else {
      groups.unshift({
        key: '_config',
        label: 'Configuração',
        fields: remainingFields,
      });
    }
  }

  const zeroGroup = groups.find(g => g.key === zeroKey);
  if (zeroGroup && zeroGroup.label !== 'Configuração') {
    zeroGroup.label = 'Geral';
  }

  return groups;
}

export function tryLetterSuffixGrouping(fields) {
  const regex = /^(\d+)[A-Z]+$/;
  let matchCount = 0;
  const groupMap = {};

  for (const field of fields) {
    const m = field.name.match(regex);
    if (m) {
      matchCount++;
      const base = m[1];
      if (!groupMap[base]) groupMap[base] = [];
      groupMap[base].push(field);
    } else if (/^\d+$/.test(field.name)) {
      matchCount++;
      const base = field.name;
      if (!groupMap[base]) groupMap[base] = [];
      groupMap[base].push(field);
    }
  }

  if (matchCount / fields.length < 0.7) return null;

  const multiFieldGroups = Object.keys(groupMap).filter(k => groupMap[k].length >= 2);
  if (multiFieldGroups.length < 3) return null;

  const groups = buildGroupsFromMap(groupMap, [], '000');
  return { grouped: true, groups };
}

export function tryNumericGrouping(fields) {
  const numericFields = [];
  const nonNumericFields = [];

  for (const field of fields) {
    const num = parseInt(field.name, 10);
    if (!isNaN(num) && /^\d+/.test(field.name)) {
      numericFields.push({ field, num });
    } else {
      nonNumericFields.push(field);
    }
  }

  if (numericFields.length < fields.length * 0.5) return null;

  let bestDivisor = 10;
  let bestScore = Infinity;

  for (const divisor of [10, 100, 1000]) {
    const seen = new Set();
    for (const { num } of numericFields) {
      seen.add(Math.floor(num / divisor));
    }
    const groupCount = seen.size;
    if (groupCount >= 3 && groupCount <= 30) {
      const score = Math.abs(groupCount - 10);
      if (score < bestScore) {
        bestScore = score;
        bestDivisor = divisor;
      }
    }
  }

  const groupMap = {};
  for (const { field, num } of numericFields) {
    const groupKey = Math.floor(num / bestDivisor);
    if (!groupMap[groupKey]) groupMap[groupKey] = [];
    groupMap[groupKey].push(field);
  }

  const groups = buildGroupsFromMap(groupMap, nonNumericFields, '0');
  if (groups.length < 3) return null;

  return { grouped: true, groups };
}
